import { useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClickOutside } from '../../lib/useClickOutside';
import { ACCOUNTS_GRID_KEY, type CurrentUser, type GridPreferences } from '../../types/user';
import { patchMyPreferences } from './api';
import {
  ACCOUNTS_COLUMNS,
  ACCOUNTS_DEFAULT_VISIBLE,
  sanitizeVisible,
  type AccountColumnKey,
} from './accountColumns';

// Stage 5A §3.4 — Accounts grid "View" menu: preset switcher + column
// checklist in one popover. Mirrors the rollout ColumnPresetMenu + GroupCogMenu
// pattern but flat (no column ordering / groups). Persists via the generalised
// preferences endpoint under gridKey 'accounts'. Column toggles are ephemeral
// against the working `visibleColumns`; the user persists them by saving a
// preset (matches the rollout pattern + spec §7.3 "toggle … save preference").

export const ACCOUNTS_DEFAULT_PRESET_ID = 'default';
const MAX_USER_PRESETS = 3;

type Props = {
  currentUser: CurrentUser;
  visibleColumns: ReadonlySet<AccountColumnKey>;
  onApply: (visible: Set<AccountColumnKey>) => void;
};

function defaultVisible(): Set<AccountColumnKey> {
  return new Set(ACCOUNTS_DEFAULT_VISIBLE);
}

function applyStored(stored: readonly string[]): Set<AccountColumnKey> {
  return new Set(sanitizeVisible(stored));
}

// Initialise the grid's working visible-set from the user's active 'accounts'
// preset (or the built-in default). Exposed so AccountsGrid seeds on mount.
export function initialAccountVisibleFromPrefs(
  prefs: GridPreferences | undefined,
): Set<AccountColumnKey> {
  if (!prefs || prefs.activePresetId === ACCOUNTS_DEFAULT_PRESET_ID) return defaultVisible();
  const p = prefs.presets.find((x) => x.id === prefs.activePresetId);
  if (!p) return defaultVisible();
  return applyStored(p.visibleColumns);
}

function snapshot(visible: ReadonlySet<AccountColumnKey>): string[] {
  // Persist in factory order, only known visible keys.
  return ACCOUNTS_COLUMNS.filter((c) => visible.has(c.key)).map((c) => c.key);
}

export function AccountsViewMenu({ currentUser, visibleColumns, onApply }: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useClickOutside([rootRef], () => setOpen(false), open);

  const prefs = currentUser.gridPreferences?.[ACCOUNTS_GRID_KEY];
  const activeId = prefs?.activePresetId ?? ACCOUNTS_DEFAULT_PRESET_ID;
  const userPresets = prefs?.presets ?? [];

  const activeLabel = useMemo(() => {
    if (activeId === ACCOUNTS_DEFAULT_PRESET_ID) return 'Default view';
    return userPresets.find((p) => p.id === activeId)?.name ?? 'Default view';
  }, [activeId, userPresets]);

  const saveMut = useMutation({
    mutationFn: (next: GridPreferences) => patchMyPreferences(ACCOUNTS_GRID_KEY, next),
    onSuccess: (user) => qc.setQueryData(['me'], user),
  });

  const persist = async (next: GridPreferences) => {
    try {
      await saveMut.mutateAsync(next);
    } catch (err) {
      console.error('Failed to save accounts grid preferences', err);
    }
  };

  const buildPrefs = (presets: typeof userPresets, nextActiveId: string): GridPreferences => ({
    activePresetId: nextActiveId,
    presets,
  });

  const switchPreset = (id: string) => {
    if (id === ACCOUNTS_DEFAULT_PRESET_ID) {
      onApply(defaultVisible());
    } else {
      const p = userPresets.find((x) => x.id === id);
      if (!p) return;
      onApply(applyStored(p.visibleColumns));
    }
    void persist(buildPrefs(userPresets, id));
  };

  const saveAsNew = () => {
    if (userPresets.length >= MAX_USER_PRESETS) return;
    const name = window.prompt('Name this view');
    if (!name || !name.trim()) return;
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `preset-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const next = [...userPresets, { id, name: name.trim(), visibleColumns: snapshot(visibleColumns) }];
    void persist(buildPrefs(next, id));
  };

  const saveChanges = () => {
    if (activeId === ACCOUNTS_DEFAULT_PRESET_ID) return;
    const next = userPresets.map((p) =>
      p.id === activeId ? { ...p, visibleColumns: snapshot(visibleColumns) } : p,
    );
    void persist(buildPrefs(next, activeId));
  };

  const deleteCurrent = () => {
    if (activeId === ACCOUNTS_DEFAULT_PRESET_ID) return;
    if (!window.confirm('Delete this view? The columns stay as they are right now.')) return;
    const next = userPresets.filter((p) => p.id !== activeId);
    void persist(buildPrefs(next, ACCOUNTS_DEFAULT_PRESET_ID));
  };

  const resetToDefault = () => {
    onApply(defaultVisible());
    void persist(buildPrefs(userPresets, ACCOUNTS_DEFAULT_PRESET_ID));
  };

  const toggleColumn = (key: AccountColumnKey) => {
    const next = new Set(visibleColumns);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onApply(next);
  };

  const atCap = userPresets.length >= MAX_USER_PRESETS;
  const isDefault = activeId === ACCOUNTS_DEFAULT_PRESET_ID;

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="rounded border border-slate-300 px-2 py-1.5 text-sm bg-white hover:bg-slate-50 text-left min-w-[180px] truncate"
        title={`View: ${activeLabel}`}
      >
        <span className="text-slate-500 text-xs mr-1">Columns</span>
        <span className="font-medium">{activeLabel}</span>
        <span className="ml-2 text-slate-400" aria-hidden>▾</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute z-30 right-0 mt-1 w-72 rounded-md border border-slate-300 bg-white shadow-lg p-2 text-sm max-h-[70vh] overflow-y-auto"
        >
          <div className="px-1 py-1 text-xs uppercase tracking-wide text-slate-500">Views</div>
          <button
            type="button"
            onClick={() => switchPreset(ACCOUNTS_DEFAULT_PRESET_ID)}
            className={`w-full text-left px-2 py-1 rounded hover:bg-slate-50 ${isDefault ? 'bg-slate-100 font-medium' : ''}`}
          >
            Default view <span className="ml-2 text-xs text-slate-400">(built-in)</span>
          </button>
          {userPresets.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => switchPreset(p.id)}
              className={`w-full text-left px-2 py-1 rounded hover:bg-slate-50 truncate ${p.id === activeId ? 'bg-slate-100 font-medium' : ''}`}
            >
              {p.name}
            </button>
          ))}

          <div className="my-2 border-t border-slate-100" />
          <button
            type="button"
            disabled={atCap}
            onClick={saveAsNew}
            className="w-full text-left px-2 py-1 rounded hover:bg-slate-50 disabled:text-slate-300 disabled:hover:bg-transparent"
            title={atCap ? 'Max 3 views' : 'Save current columns as a new view'}
          >
            Save current as new view…
          </button>
          <button
            type="button"
            disabled={isDefault}
            onClick={saveChanges}
            className="w-full text-left px-2 py-1 rounded hover:bg-slate-50 disabled:text-slate-300 disabled:hover:bg-transparent"
          >
            Save changes to current view
          </button>
          <button
            type="button"
            disabled={isDefault}
            onClick={deleteCurrent}
            className="w-full text-left px-2 py-1 rounded hover:bg-slate-50 disabled:text-slate-300 disabled:hover:bg-transparent"
          >
            Delete view
          </button>
          <button
            type="button"
            onClick={resetToDefault}
            className="w-full text-left px-2 py-1 rounded hover:bg-slate-50"
          >
            Reset to default
          </button>

          <div className="my-2 border-t border-slate-100" />
          <div className="px-1 py-1 text-xs uppercase tracking-wide text-slate-500">Columns</div>
          <div className="space-y-0.5">
            {ACCOUNTS_COLUMNS.map((c) => {
              const checked = visibleColumns.has(c.key);
              return (
                <label
                  key={c.key}
                  className={`flex items-center gap-2 px-1 py-1 rounded ${c.alwaysOn ? 'opacity-60' : 'hover:bg-slate-50 cursor-pointer'}`}
                  title={c.alwaysOn ? 'Always shown' : undefined}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={c.alwaysOn}
                    onChange={() => toggleColumn(c.key)}
                  />
                  <span className="truncate">{c.label}</span>
                </label>
              );
            })}
          </div>

          {saveMut.isError && <p className="mt-2 text-xs text-rose-600">Couldn't save. Try again.</p>}
        </div>
      )}
    </div>
  );
}
